import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import { useUserStore } from "@/store/modules/user";

/**
 * 路由配置
 * @description 定义系统所有路由规则
 */

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "Landing",
    component: () => import("@/views/landing/Index.vue"),
    meta: { title: "JoinSpark", requiresAuth: false },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/auth/Login.vue"),
    meta: { title: "登录", requiresAuth: false },
  },
  {
    path: "/agreement",
    name: "Agreement",
    component: () => import("@/views/agreement/Index.vue"),
    meta: { title: "用户协议", requiresAuth: false },
  },
  {
    path: "/payment-agreement",
    name: "paymentAgreement",
    component: () => import("@/views/agreement/PaymentAgreement.vue"),
    meta: { title: "JoinSpark付费服务协议", requiresAuth: false },
  },
  {
    path: "/privacy",
    name: "Privacy",
    component: () => import("@/views/privacy/Index.vue"),
    meta: { title: "隐私政策", requiresAuth: false },
  },
  {
    path: "/points-rules",
    name: "PointsRules",
    component: () => import("@/views/agreement/PointsRules.vue"),
    meta: { title: "积分规则", requiresAuth: false },
  },
  {
    path: "/report/detail/:task_id",
    name: "ReportDetail",
    component: () => import("@/views/report/detail.vue"),
    meta: { title: "创意报告", requiresAuth: true },
  },
  {
    path: "/report/download/:task_id",
    name: "ReportDownload",
    component: () => import("@/views/report/download.vue"),
    meta: { title: "创意报告下载", requiresAuth: false }, 
  },
  {
    path: "/app",
    component: () => import("@/views/layouts/MainLayout.vue"),
    redirect: "/app/dashboard",
    meta: { requiresAuth: false },
    children: [
      {
        path: "dashboard",
        name: "Dashboard",
        component: () => import("@/views/dashboard/index.vue"),
        meta: { title: "仪表盘", requiresAuth: false },
      },
      {
        path: "/creative/video",
        name: "VideoGeneration",
        component: () => import("@/views/generation/index.vue"),
        meta: { title: "视频创作", requiresAuth: true, creativeType: "video" },
      },
      {
        path: "/creative/image",
        name: "ImageGeneration",
        component: () => import("@/views/generation/index.vue"),
        meta: { title: "图片创作", requiresAuth: true, creativeType: "image" },
      },
      {
        path: "/community/report",
        name: "ReportList",
        component: () => import("@/views/report/index.vue"),
        meta: { title: "创意报告", requiresAuth: true },
      },
      {
        path: "/community/report/create",
        name: "CommunityReportCreate",
        component: () => import("@/views/report/create.vue"),
        meta: { title: "创意报告创建", requiresAuth: true },
      },
      {
        path: "/creative/text",
        name: "TextGeneration",
        component: () => import("@/views/generation/index.vue"),
        meta: { title: "脚本创作", requiresAuth: true, creativeType: "text" },
      },
      {
        path: "/community/list",
        name: "CommunityIndex",
        component: () => import("@/views/community/index.vue"),
        meta: { title: " 创意社区", requiresAuth: true },
      },
      {
        path: "/community/:ad_key",
        name: "CommunityDetail",
        component: () => import("@/views/community/detail.vue"),
        meta: { title: " 创意社区详情", requiresAuth: true },
      },
      {
        path: "/history/list",
        name: "HistoryList",
        component: () => import("@/views/history/list.vue"),
        meta: { title: "历史创作", requiresAuth: true },
      },
      {
        // 灵感解析详情
        path:"/inspiration/analysis/:task_id",
        name:"InspirationAnalysis",
        component:()=>import("@/views/inspiration/InspirationAnalysis.vue"),
        meta:{title:"",requireAuth:true,}
      },
      {
        path: "/material/collection",
        name: "MaterialCollection",
        component: () => import("@/views/material/collection.vue"),
        meta: { title: "创意收藏", requiresAuth: true },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/layouts/404.vue"),
    meta: { title: "页面不存在", requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

/**
 * 路由守卫
 * @description 检查用户登录状态，控制页面访问权限
 */
router.beforeEach((to, _from, next) => {
  const userStore = useUserStore();

  // 检查路由是否需要认证
  if (to.meta.requiresAuth) {
    if (!userStore.isLogin) {
      // 未登录用户访问需要认证的页面，跳转到登录页
      next("/login");
      return;
    }
  }

  // 设置页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - JoinSpark`;
  }

  next();
});

export default router;
